import clsx from 'clsx';
import { ChevronDown, Clock, Coffee, Hourglass, LogIn, LogOut, MonitorSmartphone, Pause, Play, ShieldCheck, Sparkles, Undo2 } from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useI18n, type TranslationKey } from '../lib/i18n';
import { formatDateTime, formatDays, formatMinutes, formatShortTime } from '../lib/format';
import { dismissShiftPrompt, shiftPromptDismissed } from '../lib/shift-prompt';
import { Button, ConfirmDialog, StatusDot, useToast } from './ui';

/* -------------------------------------------------------------------------- */
/* The data                                                                    */
/* -------------------------------------------------------------------------- */

export interface BreakTypeRef {
  id: string;
  code: string;
  name: string;
  nameAr: string | null;
  maxMinutes: number | null;
  /** How many times a day each employee may take it, every shift of the day counted (2026-10-28); null = no limit. */
  maxPerDay?: number | null;
  /** For the signed-in person: how many more they may take today; null = no limit. Missing from an older API. */
  leftToday?: number | null;
  /** Days of salary a break of this type costs when it runs past `maxMinutes`. 0 = none. */
  penaltyDays: string | number;
  /** Kept for team leaders and administrators; `/attendance/me` offers it to nobody else. */
  leadersOnly?: boolean;
  /** Kept for administrators; `/attendance/me` offers it to nobody else, team leaders included. */
  adminsOnly?: boolean;
  /** Its time is work (a meeting): it stays in worked time instead of coming off it. */
  countsAsWork?: boolean;
  /** Taken only once an administrator approves (Settings › Attendance › Break types). */
  requiresApproval?: boolean;
  /** For the signed-in person: pressing it asks an administrator. Whoever approves them takes it at once. */
  needsApproval?: boolean;
  /** The built-in type a pause is recorded as (Pause work, 2026-10-24) — never offered as a break. */
  isPause?: boolean;
}

/** Asking for a break whose type needs an administrator's approval, and what came of it. */
export interface BreakRequestRef {
  id: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED' | 'CANCELLED';
  requestedAt: string;
  decidedAt: string | null;
  /** Why it was not approved — written for the employee. */
  decisionNote: string | null;
  cancelReason: 'WITHDRAWN' | 'OTHER_BREAK' | 'SHIFT_ENDED' | null;
  breakId: string | null;
  breakType: BreakTypeRef;
  decidedBy: { id: string; fullName: string; fullNameAr: string | null } | null;
}

/** The request of this shift still waiting for an administrator, if there is one. */
export const waitingRequest = (data: Pick<AttendanceState, 'breakRequests'> | null | undefined): BreakRequestRef | null =>
  data?.breakRequests?.find((r) => r.status === 'PENDING') ?? null;

export interface AttendanceBreak {
  id: string;
  startedAt: string;
  endedAt: string | null;
  breakType: BreakTypeRef;
  /** Raised when the break ended past its limit — waiting for a leader or an admin, or decided. */
  penalty?: { id: string; days: string | number | null; status: string } | null;
}

/** A break past its type's limit raises a penalty when it ends — unless its type costs nothing. */
export const overrunPenalised = (type: BreakTypeRef | null | undefined): boolean =>
  !!type && type.maxMinutes != null && Number(type.penaltyDays) > 0;

/** …and unless today's hours are already worked (2026-10-21): then going past it costs nothing either. */
export const overrunCharged = (data: Pick<AttendanceState, 'hoursMet'>, type: BreakTypeRef | null | undefined): boolean =>
  overrunPenalised(type) && !data.hoursMet;

/** A break that is a pause (Pause work): its type is the built-in pause type. */
export const isPause = (b: { breakType?: BreakTypeRef | null } | null | undefined): boolean => !!b?.breakType?.isPause;

/** Taken as many times today as its daily limit allows (2026-10-28): the server refuses another, so no button offers one. */
export const usedUpToday = (type: BreakTypeRef): boolean => type.leftToday === 0;

export interface AttendanceSession {
  id: string;
  /** REGULAR — the paid shift, one a work date; FREE — tracked, unpaid work after it (CR-006). Missing from an older API: REGULAR. */
  kind?: 'REGULAR' | 'FREE';
  /** A Free Shift's: the Regular Shift it follows. */
  anchorSessionId?: string | null;
  workDate: string;
  startedAt: string;
  endedAt: string | null;
  autoClosed: boolean;
  /** Closed at its rostered end with no Desktop Agent to say when it ended: a manager sets the real one (2026-10-21). */
  endUnconfirmed?: boolean;
  closeNote: string | null;
  closedBy: { id: string; fullName: string; fullNameAr: string | null } | null;
  breaks: AttendanceBreak[];
  /** The computer whose Desktop Agent tracked it. */
  agentDevice?: { id: string; name: string } | null;
  /** Its stretches of Not working, and the inactivity penalties they raised. */
  idle?: { count: number; penalised: number; penaltyDays: number };
}

/** Why someone on shift is Not working — never a break. */
export type PresenceReason = 'IDLE_INACTIVITY' | 'DEVICE_LOCKED' | 'AGENT_DISCONNECTED' | 'DEVICE_SLEEPING' | 'UNKNOWN';

/** Working or Not working on the open shift, as the Desktop Agent shows it. */
export interface PresenceView {
  tracked: boolean;
  status: 'WORKING' | 'NOT_WORKING' | null;
  reason: PresenceReason | null;
  since: string | null;
  /** When the stretch under way raises its penalty if nobody touches the computer. */
  penaltyDueAt: string | null;
  device: { id: string; name: string; online: boolean; lastHeartbeatAt: string | null } | null;
}

/** Where someone's Desktop Agent stands. */
export type AgentStatus = 'CONNECTED' | 'OFFLINE' | 'NOT_PAIRED' | 'DISABLED';

export interface AgentInfo {
  /** No agent = no shift. */
  required: boolean;
  status: AgentStatus;
  /** May start a shift as far as the agent goes — the server checks again on Start. */
  canStart: boolean;
  device: { id: string; name: string; lastHeartbeatAt: string | null; online: boolean } | null;
  deviceCount: number;
  /** The installers — Windows (.exe) and Mac (.dmg) — set in Settings › Attendance. */
  downloadUrl: string | null;
  downloadUrlMac: string | null;
  heartbeatSeconds: number;
  idleEnabled: boolean;
  idleThresholdMinutes: number;
  penaltyEnabled: boolean;
  penaltyThresholdMinutes: number;
  penaltyDays: number;
  offlineGraceMinutes: number;
}

/** "Not working — Idle", "Not working — Agent disconnected" … */
export function presenceLabel(
  presence: Pick<PresenceView, 'status' | 'reason'> | null | undefined,
  t: (key: TranslationKey, vars?: Record<string, string | number>) => string,
): string | null {
  if (!presence?.status) return null;
  if (presence.status === 'WORKING') return t('presence.WORKING');
  return presence.reason
    ? t('presence.notWorkingAs', { reason: t(`presence.reason.${presence.reason}` as TranslationKey) })
    : t('presence.NOT_WORKING');
}

export interface DaySummary {
  /** On shift less the breaks that are rest, less pauses and less Not working. */
  workedMinutes: number;
  /** Every break, whatever its type — pauses aside. */
  breakMinutes: number;
  /** Of `breakMinutes`, the breaks whose type counts as work — left in `workedMinutes`. */
  workBreakMinutes?: number;
  /** Paused (Pause work): off worked time, and no break. Missing from an older API. */
  pausedMinutes?: number;
  /** Not working on shift (the Desktop Agent's) — neither work nor a break. */
  notWorkingMinutes: number;
  overrunMinutes: number;
  firstStartAt: string | null;
  lastEndAt: string | null;
  open: boolean;
  onBreak: boolean;
  lateMinutes: number | null;
  leftEarlyMinutes: number | null;
}

/**
 * The open shift's day against the hours it owes — shown to whoever may pause, who
 * must work them before ending their shift (operator, 2026-10-24).
 */
export interface DayHours {
  owedMinutes: number;
  workedMinutes: number;
  /** Still to work; 0 once met. */
  missingMinutes: number;
  met: boolean;
  /** Days of salary a day that ends short of its hours costs (`attendance.shortDayPenaltyDays`). */
  shortDayPenaltyDays: number;
}

/**
 * Ending the open Regular Shift early (operator, 2026-11-03): its rostered end, from when it
 * may end with no penalty, and what ending more than `allowedMinutes` before the end costs —
 * whatever the day's hours come to.
 */
export interface EarlyEndTerms {
  endAt: string;
  allowedFrom: string;
  allowedMinutes: number;
  penaltyDays: number;
}

/** One of the work date's Free Shifts, with what it came to (an open one: so far). */
export interface FreeShiftRef extends AttendanceSession {
  /** Its place after the Regular Shift it follows: #1, #2 … */
  number: number;
  summary: DaySummary;
}

/** The work date's Free Shifts (CR-006): tracked, unpaid, never in its hours. */
export interface FreeShifts {
  count: number;
  workedMinutes: number;
  notWorkingMinutes: number;
  /** One is running: its figures are so far. */
  open: boolean;
  sessions: FreeShiftRef[];
}

/** What the shift controls may offer — the server's, never worked out here (CR-006 §6.5). */
export interface ShiftActions {
  canStartRegular: boolean;
  canEndRegular: boolean;
  canStartFree: boolean;
  canEndFree: boolean;
}

export interface AttendanceState {
  now: string;
  date: string;
  /** The work date being worked: the open shift's, else the one a Regular start would get now. Missing from an older API. */
  workDate?: string;
  promptOnLogin: boolean;
  /** PAUSED: work is paused (Pause work) — `openBreak` is the pause. FREE_SHIFT: a Free Shift is running (CR-006). */
  state: 'OFF_DUTY' | 'WORKING' | 'ON_BREAK' | 'PAUSED' | 'FREE_SHIFT';
  /** The work date's one Regular Shift: not started, running, or over for good. Missing from an older API. */
  regular?: { workDate: string; status: 'NOT_STARTED' | 'ACTIVE' | 'COMPLETED'; sessionId: string | null };
  /** What may be offered now. Missing from an older API: then Start shift when off duty, as before. */
  actions?: ShiftActions;
  /** When the next Regular Shift opens, once the work date's is over. */
  nextRegularAt?: string | null;
  free?: FreeShifts;
  /** Regular, Free and Total tracked worked minutes of the work date — the total is information only. */
  tracked?: { regularMinutes: number; freeMinutes: number; totalMinutes: number };
  session: AttendanceSession | null;
  /** The break under way — or the pause, its type's `isPause` set. */
  openBreak: AttendanceBreak | null;
  /** Whether they may pause work: an administrator, or picked in Settings › Attendance. Missing from an older API. */
  pause?: { allowed: boolean };
  /** For whoever may pause, on shift: the day's hours. Null otherwise. */
  hours?: DayHours | null;
  /** On a Regular Shift whose shift charges for ending early: when it may end with no penalty. Missing from an older API. */
  earlyEnd?: EarlyEndTerms | null;
  /** Working or Not working on the open shift; null when no shift is open. */
  presence: PresenceView | null;
  /** The Desktop Agent. */
  agent: AgentInfo;
  stale: boolean;
  breakTypes: BreakTypeRef[];
  /** This shift's break requests, newest first — one waiting at most. Missing from an older API. */
  breakRequests?: BreakRequestRef[];
  /** Whether they decide others' break requests: an administrator, or picked on a break type. */
  approvesBreakRequests?: boolean;
  roster: {
    type: string;
    startAt: string | null;
    endAt: string | null;
    shiftName: string | null;
    shiftNameAr: string | null;
  } | null;
  summary: DaySummary & { sessionCount: number };
  /** Today's hours are worked already: a break past its limit raises no penalty now. Missing from an older API. */
  hoursMet?: boolean;
}

/**
 * The server's actions — or, from an API older than CR-006, what it allowed then: Start shift
 * off duty, End shift on one.
 */
export function actionsOf(data: Pick<AttendanceState, 'actions' | 'state' | 'session'>): ShiftActions {
  if (data.actions) return data.actions;
  return { canStartRegular: data.state === 'OFF_DUTY', canEndRegular: !!data.session, canStartFree: false, canEndFree: false };
}

/** A Free Shift (CR-006): tracked, unpaid work after the day's Regular Shift. */
export const isFreeShift = (s: { kind?: string } | null | undefined): boolean => s?.kind === 'FREE';

/** A shift action waiting for its yes in `ShiftConfirm`. */
export type ShiftAction =
  | { kind: 'start' }
  | { kind: 'break'; type: BreakTypeRef }
  // A break that needs approval is asked for, and withdrawn while it waits.
  | { kind: 'request'; type: BreakTypeRef }
  | { kind: 'withdraw'; request: BreakRequestRef }
  | { kind: 'resume' }
  // Pause work, for whoever may (2026-10-24), and back to work from it.
  | { kind: 'pause' }
  | { kind: 'unpause' }
  | { kind: 'end' }
  // Free Shifts (CR-006): after the day's Regular Shift, as many as wanted, one at a time.
  | { kind: 'startFree' }
  | { kind: 'endFree'; sessionId: string };

type Action = { kind: ShiftAction['kind']; path: string; body?: Record<string, unknown> };

/** Where each action is sent. Every one answers with the shift as it now stands. */
function toAction(action: ShiftAction): Action {
  switch (action.kind) {
    case 'break':
      return { kind: 'break', path: 'break', body: { breakTypeId: action.type.id } };
    case 'request':
      return { kind: 'request', path: 'break-requests', body: { breakTypeId: action.type.id } };
    case 'withdraw':
      return { kind: 'withdraw', path: `break-requests/${action.request.id}/cancel` };
    // Back to work ends a pause as it ends a break.
    case 'unpause':
      return { kind: 'unpause', path: 'resume' };
    case 'startFree':
      return { kind: 'startFree', path: 'free/start' };
    case 'endFree':
      return { kind: 'endFree', path: `free/${action.sessionId}/end` };
    default:
      return { kind: action.kind, path: action.kind };
  }
}

/**
 * The signed-in person's shift, shared by the top bar, the prompt and the
 * Attendance page — one query, so pressing a button anywhere updates all three.
 */
export function useAttendance() {
  const { t } = useI18n();
  const toast = useToast();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['attendance', 'me'],
    queryFn: () => api.get<AttendanceState>('/attendance/me'),
    // Presence moves on its own while a shift runs (the agent reports every half
    // minute), so the console looks again more often then.
    refetchInterval: (q) => (q.state.data?.session ? 20_000 : 60_000),
    staleTime: 15_000,
  });

  const messages: Record<Action['kind'], string> = {
    start: t('attendance.shiftStarted'),
    break: t('attendance.breakStarted'),
    request: t('attendance.requestSent'),
    withdraw: t('attendance.requestWithdrawn'),
    resume: t('attendance.backToWork'),
    pause: t('attendance.workPaused'),
    unpause: t('attendance.backToWork'),
    end: t('attendance.shiftEnded'),
    startFree: t('attendance.freeStarted'),
    endFree: t('attendance.freeEnded'),
  };

  const act = useMutation({
    mutationFn: ({ path, body }: Action) => api.post<AttendanceState>(`/attendance/${path}`, body ?? {}),
    onSuccess: (data, { kind }) => {
      queryClient.setQueryData(['attendance', 'me'], data);
      void queryClient.invalidateQueries({ queryKey: ['attendance', 'history'] });
      void queryClient.invalidateQueries({ queryKey: ['attendance', 'board'] });
      toast.success(messages[kind]);
    },
    onError: (e: Error & { code?: string; details?: any }) => {
      // A menu older than the break type's switch to leaders only.
      if (e.code === 'BREAK_TYPE_LEADERS_ONLY') toast.error(t('attendance.breakLeadersOnly'));
      // A menu older than the type's switch to administrators only.
      else if (e.code === 'BREAK_TYPE_ADMINS_ONLY') toast.error(t('attendance.breakAdminsOnly'));
      // A menu older than the type's being kept for other departments.
      else if (e.code === 'BREAK_TYPE_DEPARTMENTS_ONLY') toast.error(t('attendance.breakDepartmentsOnly'));
      // A menu older than the type's being kept for other employees.
      else if (e.code === 'BREAK_TYPE_EMPLOYEES_ONLY') toast.error(t('attendance.breakEmployeesOnly'));
      // A menu older than the type's switch to needing approval.
      else if (e.code === 'BREAK_NEEDS_APPROVAL') toast.error(t('attendance.breakNeedsApproval'));
      // A menu older than the last break of the type its daily limit allowed (another tab took it).
      else if (e.code === 'BREAK_DAILY_LIMIT') toast.error(t('attendance.breakDailyLimit'));
      else if (e.code === 'BREAK_REQUEST_PENDING') toast.error(t('attendance.requestAlreadyWaiting'));
      // Work is paused (another tab paused it): getting back to work comes first.
      else if (e.code === 'ATTENDANCE_PAUSED') toast.error(t('attendance.pausedFirst'));
      // Taken off the list of those who may pause since this page loaded.
      else if (e.code === 'PAUSE_NOT_ALLOWED') toast.error(t('attendance.pauseNotAllowed'));
      // No Desktop Agent = no shift: say what is missing, in the reader's language.
      else if (e.code === 'AGENT_REQUIRED') toast.error(t(agentBlockKey(e.details?.reason)));
      // One Regular Shift a work date, and Free Shifts after it (CR-006): another tab moved on, or a menu is older than it.
      else if (e.code === 'REGULAR_ALREADY_STARTED_OR_COMPLETED') toast.error(t('attendance.regularTaken'));
      else if (e.code === 'ACTIVE_SESSION_EXISTS') toast.error(t('attendance.activeSessionExists'));
      else if (e.code === 'FREE_REQUIRES_COMPLETED_REGULAR') toast.error(t('attendance.freeNeedsRegular'));
      else if (e.code === 'FREE_OUTSIDE_ELIGIBILITY_WINDOW') toast.error(t('attendance.freeWindowPassed'));
      else if (e.code === 'WRONG_SESSION_TYPE') toast.error(t('attendance.wrongSessionType'));
      else if (e.code === 'SESSION_ALREADY_ENDED') toast.error(t('attendance.sessionAlreadyEnded'));
      else toast.error(e.message);
      // The server knows better than this tab — another tab may have moved on.
      void queryClient.invalidateQueries({ queryKey: ['attendance', 'me'] });
    },
  });

  return {
    ...query,
    busy: act.isPending,
    /** Only once it is confirmed — `ShiftConfirm` calls it. `onSettled` runs when the server has answered, either way. */
    perform: (action: ShiftAction, onSettled?: () => void) => act.mutate(toAction(action), { onSettled }),
  };
}

/**
 * Every shift action asks first, wherever it is pressed: the top bar's menu,
 * the sign-in prompt and My shift (operator, 2026-10-15: "when click on any
 * option show approve dialog message"). It names what was picked, so a break
 * hit by mistake is caught before its clock starts, and says what follows.
 */
export function ShiftConfirm({
  action,
  attendance,
  onClose,
}: {
  action: ShiftAction | null;
  attendance: ReturnType<typeof useAttendance>;
  onClose: () => void;
}) {
  const { t, locale, localized } = useI18n();
  const data = attendance.data;
  const openBreak = data?.openBreak ?? null;
  // Only Back to work reads how long the break has run, so only it keeps a clock.
  const breakSeconds = useElapsed(action?.kind === 'resume' ? openBreak?.startedAt : null, data?.now);
  if (!action || !data) return null;

  const ask = shiftQuestion(action, {
    t,
    locale,
    localized,
    stale: data.stale,
    openBreak,
    breakOver: openBreak?.breakType.maxMinutes != null && breakSeconds > openBreak.breakType.maxMinutes * 60,
    waiting: waitingRequest(data),
    hoursMet: !!data.hoursMet,
    hours: data.hours ?? null,
    earlyEnd: data.earlyEnd ?? null,
    // The server's clock, as the live clocks read it: set by how far this tab's was from it when it last asked.
    now: Date.now() + (new Date(data.now).getTime() - attendance.dataUpdatedAt),
  });

  return (
    <ConfirmDialog
      title={ask.title}
      confirmLabel={ask.confirm}
      danger={action.kind === 'end' || action.kind === 'endFree'}
      busy={attendance.busy}
      onConfirm={() => attendance.perform(action, onClose)}
      onCancel={onClose}
    >
      {ask.lines.map((line) => (
        <p key={line}>{line}</p>
      ))}
      {ask.warnings?.map((line) => (
        <p key={line} className="text-error-fg dark:text-error-dark">
          {line}
        </p>
      ))}
    </ConfirmDialog>
  );
}

/** What `ShiftConfirm` asks, and what it says follows, for each action. */
function shiftQuestion(
  action: ShiftAction,
  {
    t,
    locale,
    localized,
    stale,
    openBreak,
    breakOver,
    waiting,
    hoursMet,
    hours,
    earlyEnd,
    now,
  }: Pick<ReturnType<typeof useI18n>, 't' | 'locale' | 'localized'> & {
    stale: boolean;
    openBreak: AttendanceBreak | null;
    breakOver: boolean;
    /** A request of this shift still waiting for an administrator. */
    waiting: BreakRequestRef | null;
    /** Today's hours are worked already: going past a limit costs nothing now. */
    hoursMet: boolean;
    /** The day's hours, for whoever may pause — who must work them before ending (2026-10-24). */
    hours: DayHours | null;
    /** What ending the Regular Shift early costs, when its shift says (2026-11-03). */
    earlyEnd: EarlyEndTerms | null;
    /** The server's time now, in milliseconds. */
    now: number;
  },
): { title: string; confirm: string; lines: string[]; warnings?: string[] } {
  const span = (minutes: number) => formatMinutes(minutes, locale);
  // Where the day stands, for someone who may pause: worked so far, and what is left to work.
  const standing = hours
    ? [
        hours.met
          ? t('attendance.hoursDone', { owed: span(hours.owedMinutes) })
          : t('attendance.hoursLeft', {
              worked: span(hours.workedMinutes),
              owed: span(hours.owedMinutes),
              missing: span(hours.missingMinutes),
            }),
      ]
    : [];
  const shortCosts = hours && !hours.met && Number(hours.shortDayPenaltyDays) > 0;
  // What a break of this type allows, costs past its limit, how many are left of it today, and
  // what it does to worked time.
  const terms = (type: BreakTypeRef) => {
    const limit = `${type.maxMinutes} ${t('common.minutesShort')}`;
    return [
      ...(type.maxMinutes != null
        ? [
            overrunCharged({ hoursMet }, type)
              ? t('attendance.breakConfirmPenalty', { limit, days: formatDays(type.penaltyDays, locale) })
              : t('attendance.breakConfirmLimit', { limit }),
          ]
        : []),
      ...(type.maxPerDay != null && type.leftToday != null
        ? [t('attendance.breakConfirmPerDay', { max: type.maxPerDay, left: type.leftToday })]
        : []),
      t(type.countsAsWork ? 'attendance.breakConfirmWork' : 'attendance.breakConfirmRest'),
    ];
  };
  switch (action.kind) {
    case 'start':
      return {
        title: t('attendance.startConfirm'),
        confirm: t('attendance.startShift'),
        // Once a work date (CR-006) — and a shift left open is closed where it should have ended, the prompt's own note.
        lines: [t('attendance.startConfirmBody'), t('attendance.startConfirmOnce'), ...(stale ? [t('attendance.staleNote')] : [])],
      };
    case 'startFree':
      return {
        title: t('attendance.startFreeConfirm'),
        confirm: t('attendance.startFree'),
        lines: [t('attendance.startFreeConfirmBody'), ...(stale ? [t('attendance.staleNote')] : [])],
      };
    case 'endFree':
      return {
        title: t('attendance.endFreeConfirm'),
        confirm: t('attendance.endFree'),
        lines: [t('attendance.endFreeConfirmBody')],
      };
    case 'break':
      return {
        title: t('attendance.breakConfirm', { name: localized(action.type) }),
        confirm: t('attendance.breakConfirmAction'),
        lines: [
          ...terms(action.type),
          // Another break takes the place of the one asked for.
          ...(waiting ? [t('attendance.breakWithdrawsRequest', { name: localized(waiting.breakType) })] : []),
        ],
      };
    case 'request':
      return {
        title: t('attendance.requestConfirm', { name: localized(action.type) }),
        confirm: t('attendance.requestConfirmAction'),
        lines: [t('attendance.requestConfirmBody'), ...terms(action.type)],
      };
    case 'withdraw':
      return {
        title: t('attendance.withdrawConfirm', { name: localized(action.request.breakType) }),
        confirm: t('attendance.withdrawRequest'),
        lines: [t('attendance.withdrawConfirmBody')],
      };
    case 'resume':
      return {
        title: t('attendance.resumeConfirm'),
        confirm: t('attendance.resume'),
        lines: openBreak ? [t('attendance.resumeConfirmBody', { name: localized(openBreak.breakType) })] : [],
        warnings:
          openBreak && breakOver && overrunCharged({ hoursMet }, openBreak.breakType)
            ? [t('attendance.overLimitNow', { days: formatDays(openBreak.breakType.penaltyDays, locale) })]
            : undefined,
      };
    // The clock stops until they get back to work: no limit, but the day's hours are still owed.
    case 'pause':
      return {
        title: t('attendance.pauseConfirm'),
        confirm: t('attendance.pauseWork'),
        lines: [
          t('attendance.pauseConfirmBody'),
          ...standing,
          ...(shortCosts ? [t('attendance.shortDayCosts', { days: formatDays(hours!.shortDayPenaltyDays, locale) })] : []),
          // A break asked for and still waiting stops waiting, as another break would make it.
          ...(waiting ? [t('attendance.breakWithdrawsRequest', { name: localized(waiting.breakType) })] : []),
        ],
      };
    case 'unpause':
      return {
        title: t('attendance.resumeConfirm'),
        confirm: t('attendance.resume'),
        lines: [t('attendance.unpauseConfirmBody'), ...standing],
      };
    case 'end': {
      // More whole minutes before the shift's end than it allows — the API's own count (`earlyEndCharge`).
      const endsEarly =
        !!earlyEnd && Math.floor((new Date(earlyEnd.endAt).getTime() - now) / 60_000) > earlyEnd.allowedMinutes;
      return {
        title: t('attendance.endConfirm'),
        confirm: t('attendance.endShift'),
        lines: [
          t('attendance.endConfirmBody'),
          ...(openBreak
            ? [isPause(openBreak) ? t('attendance.endConfirmPause') : t('attendance.endConfirmBreak', { name: localized(openBreak.breakType) })]
            : []),
        ],
        warnings: [
          // Whoever may pause must work the day's hours before ending (operator, 2026-10-24): said before they do.
          ...(shortCosts
            ? [
                t('attendance.endShortWarning', {
                  worked: span(hours!.workedMinutes),
                  owed: span(hours!.owedMinutes),
                  days: formatDays(hours!.shortDayPenaltyDays, locale),
                }),
              ]
            : []),
          // Ending before the shift allows costs its penalty, hours worked or not (operator, 2026-11-03).
          ...(endsEarly
            ? [
                t('attendance.endEarlyWarning', {
                  end: formatShortTime(earlyEnd!.endAt, locale),
                  from: formatShortTime(earlyEnd!.allowedFrom, locale),
                  days: formatDays(earlyEnd!.penaltyDays, locale),
                }),
              ]
            : []),
          // A day both short and ended early pays one penalty, the larger (the operator's choice).
          ...(shortCosts && endsEarly ? [t('attendance.endOnePenalty')] : []),
        ],
      };
    }
  }
}

/** The sentence for a start refused for want of the Desktop Agent. */
export function agentBlockKey(reason: string | null | undefined): TranslationKey {
  return reason === 'DISABLED'
    ? 'agent.startBlocked.DISABLED'
    : reason === 'OFFLINE'
      ? 'agent.startBlocked.OFFLINE'
      : 'agent.startBlocked.NOT_PAIRED';
}

/** Re-renders every second while `active`, for the live clocks. */
function useTick(active: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [active]);
  return now;
}

/**
 * Seconds since `since`, on the server's clock: the offset between the server's
 * `now` and this machine's is taken once, so a workstation clock that is off does
 * not make a break look longer or shorter than it is.
 */
export function useElapsed(since: string | null | undefined, serverNow: string | undefined): number {
  const tick = useTick(!!since);
  const offset = useRef(0);
  useEffect(() => {
    if (serverNow) offset.current = new Date(serverNow).getTime() - Date.now();
  }, [serverNow]);
  if (!since) return 0;
  return Math.max(0, Math.floor((tick + offset.current - new Date(since).getTime()) / 1000));
}

export function formatClock(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  const mm = String(m).padStart(2, '0');
  const ss = String(s).padStart(2, '0');
  return h > 0 ? `${h}:${mm}:${ss}` : `${m}:${ss}`;
}

/* -------------------------------------------------------------------------- */
/* The top bar                                                                 */
/* -------------------------------------------------------------------------- */

/** Status, a live clock, and every shift action — two clicks from anywhere. */
export function ShiftControl() {
  const { t, locale, localized } = useI18n();
  const attendance = useAttendance();
  const [open, setOpen] = useState(false);
  const [asking, setAsking] = useState<ShiftAction | null>(null);
  const boxRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const data = attendance.data;

  const sessionSeconds = useElapsed(data?.session?.startedAt, data?.now);
  const breakSeconds = useElapsed(data?.openBreak?.startedAt, data?.now);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  if (!data) return null;

  const onBreak = data.state === 'ON_BREAK';
  // Pause work (2026-10-24): the clock stops until they get back to work.
  const paused = data.state === 'PAUSED';
  const working = data.state === 'WORKING';
  // A Free Shift (CR-006): tracked, unpaid, no breaks — End Free Shift ends it.
  const free = data.state === 'FREE_SHIFT';
  // On shift, but the Desktop Agent shows them away from their computer.
  const notWorking = (working || free) && data.presence?.status === 'NOT_WORKING';
  const breakOver =
    onBreak && data.openBreak?.breakType.maxMinutes != null && breakSeconds > data.openBreak.breakType.maxMinutes * 60;
  const actions = actionsOf(data);
  // The day's Regular Shift is over and no Free Shift is offered or running: nothing to start.
  const done = data.state === 'OFF_DUTY' && !actions.canStartRegular && !actions.canStartFree;
  const blocked = data.state === 'OFF_DUTY' && !done && !data.agent.canStart;
  // A break asked for, waiting for an administrator: one at a time.
  const waiting = working ? waitingRequest(data) : null;
  // Whoever may pause owes the day's hours all the same: what is left of them, at a glance.
  const hours = data.session ? (data.hours ?? null) : null;

  const label = onBreak
    ? localized(data.openBreak!.breakType)
    : paused
      ? t('attendance.paused')
      : notWorking
        ? t('presence.NOT_WORKING')
        : free
          ? t('attendance.freeShift')
          : working
            ? t('attendance.working')
            : t('attendance.offDuty');
  const clock = onBreak || paused ? formatClock(breakSeconds) : working || free ? formatClock(sessionSeconds) : null;

  // The menu closes onto its button, so the dialog gives the focus back there.
  const ask = (action: ShiftAction) => {
    setOpen(false);
    buttonRef.current?.focus();
    setAsking(action);
  };

  return (
    <div ref={boxRef} className="relative">
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="menu"
        className={clsx(
          'flex h-9 items-center gap-2 rounded-md border hairline px-2.5 text-body-sm font-medium',
          'transition-colors duration-fast hover:bg-[var(--surface-sunken)]',
          data.state === 'OFF_DUTY' ? 'text-[var(--text-secondary)]' : 'text-[var(--text-primary)]',
        )}
      >
        <StatusDot
          tone={onBreak || paused ? 'idle' : notWorking ? 'alert' : working || free ? 'online' : 'offline'}
          pulse={(working || free) && !notWorking}
        />
        <span className="hidden max-w-[9rem] truncate sm:inline">{label}</span>
        {clock && (
          <span className={clsx('tabular text-caption', breakOver ? 'text-error-fg dark:text-error-dark' : 'text-muted')} dir="ltr">
            {clock}
          </span>
        )}
        <ChevronDown className="h-3.5 w-3.5 text-[var(--text-tertiary)]" aria-hidden />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute end-0 top-full z-dropdown mt-1.5 w-64 overflow-hidden rounded-md surface shadow-lg"
        >
          <div className="border-b hairline px-3 py-2.5">
            <p className="text-body-sm font-medium text-[var(--text-primary)]">{label}</p>
            <p className="text-caption text-muted">
              {free && data.session
                ? t('attendance.freeSince', { time: formatShortTime(data.session.startedAt, locale) })
                : data.session
                ? t(notWorking ? 'attendance.shiftSince' : 'attendance.since', {
                    time: formatShortTime(onBreak || paused ? data.openBreak!.startedAt : data.session.startedAt, locale),
                  })
                : done || actions.canStartFree
                  ? data.nextRegularAt
                    ? t('attendance.nextRegularAt', { time: formatDateTime(data.nextRegularAt, locale) })
                    : t('attendance.regularDone')
                : data.roster?.startAt
                  ? t('attendance.promptRoster', {
                      shift: localized({ name: data.roster.shiftName ?? '', nameAr: data.roster.shiftNameAr }),
                      start: formatShortTime(data.roster.startAt, locale),
                      end: formatShortTime(data.roster.endAt, locale),
                    })
                  : t('attendance.promptBody')}
            </p>
            {breakOver && overrunCharged(data, data.openBreak?.breakType) && (
              <p className="mt-1 text-caption text-error-fg dark:text-error-dark">
                {t('attendance.overLimitShort', { days: formatDays(data.openBreak!.breakType.penaltyDays, locale) })}
              </p>
            )}
            {hours && (
              <p className={clsx('mt-1 text-caption tabular', hours.met ? 'text-success-fg dark:text-success' : 'text-muted')}>
                {hours.met
                  ? t('attendance.hoursDoneShort')
                  : t('attendance.hoursLeftShort', { missing: formatMinutes(hours.missingMinutes, locale) })}
              </p>
            )}
            {free && <p className="mt-1 text-caption text-muted">{t('attendance.freeUnpaid')}</p>}
            {(working || free) && data.presence?.tracked && (
              <p className={clsx('mt-1 text-caption', notWorking ? 'text-error-fg dark:text-error-dark' : 'text-muted')}>
                {notWorking
                  ? `${presenceLabel(data.presence, t)} · ${t('attendance.since', { time: formatShortTime(data.presence.since, locale) })}`
                  : t('presence.trackedOn', { name: data.presence.device?.name ?? '' })}
              </p>
            )}
            {notWorking && data.presence?.penaltyDueAt && (
              <p className="mt-1 text-caption text-error-fg dark:text-error-dark">
                {t('presence.penaltyAt', {
                  days: formatDays(data.agent.penaltyDays, locale),
                  time: formatShortTime(data.presence.penaltyDueAt, locale),
                })}
              </p>
            )}
            {blocked && <p className="mt-1 text-caption text-warning-fg dark:text-warning-dark">{t(agentBlockKey(data.agent.status))}</p>}
            {waiting && (
              <p className="mt-1 text-caption text-warning-fg dark:text-warning-dark">
                {t('attendance.requestWaiting', { name: localized(waiting.breakType) })} ·{' '}
                {t('attendance.askedAt', { time: formatShortTime(waiting.requestedAt, locale) })}
              </p>
            )}
          </div>

          <div className="p-1.5">
            {data.state === 'OFF_DUTY' &&
              !done &&
              (blocked ? (
                <Link
                  to="/attendance"
                  onClick={() => setOpen(false)}
                  className="flex items-center gap-2.5 rounded-sm px-2.5 py-2 text-body-sm text-primary-600 dark:text-primary-400 hover:bg-[var(--surface-sunken)]"
                >
                  <MonitorSmartphone className="h-4 w-4" aria-hidden />
                  {t('agent.setUp')}
                </Link>
              ) : actions.canStartRegular ? (
                <MenuItem icon={<LogIn className="h-4 w-4 flip-rtl" />} onClick={() => ask({ kind: 'start' })}>
                  {t('attendance.startShift')}
                </MenuItem>
              ) : (
                <MenuItem icon={<Sparkles className="h-4 w-4" />} onClick={() => ask({ kind: 'startFree' })}>
                  {t('attendance.startFree')}
                </MenuItem>
              ))}

            {free && data.session && (
              <MenuItem
                icon={<LogOut className="h-4 w-4 flip-rtl" />}
                onClick={() => ask({ kind: 'endFree', sessionId: data.session!.id })}
                danger
              >
                {t('attendance.endFree')}
              </MenuItem>
            )}

            {working && data.pause?.allowed && (
              <MenuItem icon={<Pause className="h-4 w-4" />} onClick={() => ask({ kind: 'pause' })}>
                {t('attendance.pauseWork')}
              </MenuItem>
            )}

            {paused && (
              <MenuItem icon={<Play className="h-4 w-4 flip-rtl" />} onClick={() => ask({ kind: 'unpause' })}>
                {t('attendance.resume')}
              </MenuItem>
            )}

            {working && (
              <>
                <p className="px-2.5 pb-1 pt-1.5 text-overline uppercase text-[var(--text-tertiary)]">
                  {t('attendance.takeBreak')}
                </p>
                {data.breakTypes.length === 0 && (
                  <p className="px-2.5 py-1.5 text-caption text-muted">{t('attendance.noBreakTypes')}</p>
                )}
                {data.breakTypes.map((type) => {
                  // What is left of its daily limit (2026-10-28) — and with none left, it cannot be pressed.
                  const usedUp = usedUpToday(type);
                  const left = type.leftToday != null ? t('attendance.leftShort', { n: type.leftToday }) : null;
                  return type.needsApproval ? (
                    // Asked for, not started: an administrator approves it first.
                    <MenuItem
                      key={type.id}
                      icon={waiting?.breakType.id === type.id ? <Hourglass className="h-4 w-4" /> : <ShieldCheck className="h-4 w-4" />}
                      hint={
                        waiting?.breakType.id === type.id
                          ? t('attendance.waitingShort')
                          : usedUp
                            ? t('attendance.usedUpToday')
                            : [t('attendance.needsApproval'), left].filter(Boolean).join(' · ')
                      }
                      disabled={!!waiting || usedUp}
                      onClick={() => ask({ kind: 'request', type })}
                    >
                      {localized(type)}
                    </MenuItem>
                  ) : (
                    <MenuItem
                      key={type.id}
                      icon={<Coffee className="h-4 w-4" />}
                      hint={
                        usedUp
                          ? t('attendance.usedUpToday')
                          : [type.maxMinutes ? `${type.maxMinutes} ${t('common.minutesShort')}` : null, left].filter(Boolean).join(' · ') ||
                            undefined
                      }
                      disabled={usedUp}
                      onClick={() => ask({ kind: 'break', type })}
                    >
                      {localized(type)}
                    </MenuItem>
                  );
                })}
                {waiting && (
                  <MenuItem icon={<Undo2 className="h-4 w-4 flip-rtl" />} onClick={() => ask({ kind: 'withdraw', request: waiting })}>
                    {t('attendance.withdrawRequest')}
                  </MenuItem>
                )}
              </>
            )}

            {onBreak && (
              <MenuItem icon={<Play className="h-4 w-4 flip-rtl" />} onClick={() => ask({ kind: 'resume' })}>
                {t('attendance.resume')}
              </MenuItem>
            )}

            {data.session && !free && (
              <div className="mt-1 border-t hairline pt-1">
                <MenuItem icon={<LogOut className="h-4 w-4 flip-rtl" />} onClick={() => ask({ kind: 'end' })} danger>
                  {t('attendance.endShift')}
                </MenuItem>
              </div>
            )}

            <div className="mt-1 border-t hairline pt-1">
              <Link
                to="/attendance"
                onClick={() => setOpen(false)}
                className="flex items-center gap-2.5 rounded-sm px-2.5 py-2 text-body-sm text-[var(--text-secondary)] hover:bg-[var(--surface-sunken)] hover:text-[var(--text-primary)]"
              >
                <Clock className="h-4 w-4" aria-hidden />
                {t('attendance.title')}
              </Link>
            </div>
          </div>
        </div>
      )}

      <ShiftConfirm action={asking} attendance={attendance} onClose={() => setAsking(null)} />
    </div>
  );
}

function MenuItem({
  icon,
  children,
  hint,
  danger = false,
  disabled = false,
  onClick,
}: {
  icon: ReactNode;
  children: ReactNode;
  hint?: string;
  danger?: boolean;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      disabled={disabled}
      className={clsx(
        'flex w-full items-center gap-2.5 rounded-sm px-2.5 py-2 text-start text-body-sm',
        'transition-colors duration-fast hover:bg-[var(--surface-sunken)]',
        'disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:bg-transparent',
        danger ? 'text-error-fg dark:text-error-dark' : 'text-[var(--text-primary)]',
      )}
    >
      <span className="shrink-0 text-[var(--text-tertiary)]" aria-hidden>
        {icon}
      </span>
      <span className="flex-1 truncate">{children}</span>
      {hint && <span className="text-caption text-muted tabular">{hint}</span>}
    </button>
  );
}

/* -------------------------------------------------------------------------- */
/* The prompt                                                                  */
/* -------------------------------------------------------------------------- */

/**
 * Everyone who signs in and has not started their shift sees this at the top of
 * every page until they start it — or say "not now", which lasts until they
 * next sign in. Switched off with `attendance.promptOnLogin`.
 */
export function ShiftPrompt() {
  const { t, locale, localized } = useI18n();
  const attendance = useAttendance();
  const [dismissed, setDismissed] = useState(shiftPromptDismissed);
  const [asking, setAsking] = useState<ShiftAction | null>(null);
  const data = attendance.data;

  if (!data || !data.promptOnLogin || dismissed) return null;
  // Its Regular Shift over (CR-006), the day takes no other: nothing to prompt for.
  const needsStart = data.state === 'OFF_DUTY' && actionsOf(data).canStartRegular;
  if (!needsStart && !data.stale) return null;

  const dismiss = () => {
    dismissShiftPrompt();
    setDismissed(true);
  };

  const roster = data.roster?.startAt
    ? t('attendance.promptRoster', {
        shift: localized({ name: data.roster.shiftName ?? '', nameAr: data.roster.shiftNameAr }),
        start: formatShortTime(data.roster.startAt, locale),
        end: formatShortTime(data.roster.endAt, locale),
      })
    : null;

  // No agent = no shift: the prompt says what is missing instead of offering a Start that would be refused.
  const blocked = needsStart && !data.stale && !data.agent.canStart;

  return (
    <div
      role="region"
      aria-label={t('attendance.title')}
      className="mb-5 flex flex-wrap items-center gap-x-4 gap-y-3 rounded-lg border border-primary/35 bg-primary/8 px-4 py-3.5"
    >
      <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-primary/15 text-primary-600 dark:text-primary-400" aria-hidden>
        <Clock className="h-4.5 w-4.5" />
      </span>
      {/* A real starting width, not flex-1's zero: on a phone the buttons then
          wrap under the text instead of squeezing it to a word a line. */}
      <div className="min-w-0 grow basis-48">
        <p className="text-title text-[var(--text-primary)]">
          {data.stale ? t('attendance.stillOpen') : t('attendance.promptTitle')}
        </p>
        <p className="text-body-sm text-muted">
          {data.stale
            ? t('attendance.staleNote')
            : blocked
              ? t(agentBlockKey(data.agent.status))
              : [roster, t('attendance.promptBody')].filter(Boolean).join(' ')}
        </p>
      </div>
      <div className="flex flex-wrap gap-2">
        {blocked ? (
          <Link to="/attendance">
            <Button variant="primary" icon={<MonitorSmartphone className="h-4 w-4" />}>
              {t('agent.setUp')}
            </Button>
          </Link>
        ) : (
          <Button
            variant="primary"
            icon={<LogIn className="h-4 w-4 flip-rtl" />}
            loading={attendance.busy}
            onClick={() => setAsking({ kind: 'start' })}
          >
            {t('attendance.startShift')}
          </Button>
        )}
        {data.stale && (
          <Button
            icon={<LogOut className="h-4 w-4 flip-rtl" />}
            disabled={attendance.busy}
            onClick={() =>
              setAsking(isFreeShift(data.session) && data.session ? { kind: 'endFree', sessionId: data.session.id } : { kind: 'end' })
            }
          >
            {isFreeShift(data.session) ? t('attendance.endFree') : t('attendance.endShift')}
          </Button>
        )}
        <Button variant="ghost" onClick={dismiss}>
          {t('attendance.notNow')}
        </Button>
      </div>
      <ShiftConfirm action={asking} attendance={attendance} onClose={() => setAsking(null)} />
    </div>
  );
}
